import { CloseSharpIcon } from '@assets/icons';
import {
  Autocomplete,
  Chip,
  FormHelperText,
  FormLabel,
  Grid,
  TextField,
} from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './TagInputField.scss';

export interface Option {
  label: string;
}

export interface AppAutoCompleteProps {
  id?: string;
  label?: string;
  required?: boolean;
  isRow?: boolean;
  limitTags?: number;
  options?: Option[] | string[];
  placeholder?: string;
  valueType?: 'string' | 'array';
  value?: string | string[];
  defaultValue?: string | string[];
  onChange?: (value: string | string[]) => void;
  error?: {
    type?: string;
    message?: string;
  };
  disabled?: boolean;
}

const getOptionLabel = (option: string | Option): string =>
  typeof option === 'string' ? option : option.label;

const TagInputField: FC<AppAutoCompleteProps> = ({
  id,
  required = false,
  label,
  isRow = false,
  limitTags,
  options = [],
  placeholder,
  valueType = 'string',
  value,
  defaultValue,
  onChange,
  error,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<string[]>([]);

  const errorText = useMemo(() => {
    if (error?.type === 'required') {
      return t('common.please_select', { 0: t(label ?? '') });
    }
    return error?.message ? t(error.message) : null;
  }, [error, label, t]);

  useEffect(() => {
    if (defaultValue) {
      setValues(
        typeof defaultValue === 'string'
          ? defaultValue.split(',')
          : defaultValue
      );
    }
  }, [defaultValue]);

  useEffect(() => {
    if (value) {
      setValues(typeof value === 'string' ? value.split(',') : value);
    } else {
      setValues([]);
    }
  }, [value]);

  const filteredOptions = useMemo<(string | Option)[]>(() => {
    const normalizedOptions: (string | Option)[] = options ?? [];

    return normalizedOptions.filter(
      (option: string | Option) => !values.includes(getOptionLabel(option))
    );
  }, [options, values]);

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: (string | Option)[]
  ): void => {
    const newValues = newValue.map(getOptionLabel);
    setValues(newValues);
    onChange?.(valueType === 'string' ? newValues.join(',') : newValues);
  };

  const handleDelete = (option: string | Option): void => {
    const deleteValue = getOptionLabel(option);
    const newValues = values.filter(val => val !== deleteValue);
    setValues(newValues);
    onChange?.(valueType === 'string' ? newValues.join(',') : newValues);
  };

  return (
    <Grid
      container
      className='TagInputField'
      direction={isRow ? 'row' : 'column'}
      spacing={isRow ? 3 : 2}
    >
      {label && (
        <Grid item xs={4}>
          <FormLabel htmlFor={id} required={required}>
            {t(label)}
          </FormLabel>
        </Grid>
      )}

      <Grid item xs={8} sx={{ width: '100%' }}>
        <Autocomplete
          id={id}
          multiple
          size='small'
          limitTags={limitTags}
          options={filteredOptions}
          noOptionsText={null}
          value={values}
          getOptionLabel={getOptionLabel}
          onChange={handleChange}
          disabled={disabled}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={getOptionLabel(option)}
                  {...tagProps}
                  color='primary'
                  deleteIcon={<CloseSharpIcon />}
                  onDelete={() => handleDelete(option)}
                />
              );
            })
          }
          renderInput={params => (
            <TextField
              {...params}
              placeholder={
                placeholder && !values.length ? t(placeholder) : undefined
              }
              inputProps={{
                ...params.inputProps,
                readOnly: true,
              }}
            />
          )}
        />

        {errorText && <FormHelperText error>{errorText}</FormHelperText>}
      </Grid>
    </Grid>
  );
};

export default TagInputField;
